/**
 * ProfileIncompleteBanner — F-002 Phase 4 (IMPL-P4-011).
 *
 * Banner full-width riusabile, montato dal caller (es. F-003 NuovaPraticaPage)
 * quando il profilo di fatturazione è incompleto e blocca il flusso. È
 * volutamente PURO: non chiama `useBillingProfile()` — il caller decide
 * quando mostrarlo, evitando la dipendenza forzata dal Provider.
 *
 * Props:
 *  - `returnTo?` — path a cui tornare dopo aver completato il profilo
 *    (default: `location.pathname` lato browser). Aggiunto al link come
 *    `?return=<encoded>` per consentire un eventuale redirect post-save.
 */
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

export interface ProfileIncompleteBannerProps {
  /**
   * Path da passare in `?return=` al link "Completa il profilo". Se omesso,
   * usa la `location.pathname` corrente (utile quando il banner è inline
   * nella pagina che vogliamo riprendere dopo il save).
   */
  returnTo?: string;
}

export function ProfileIncompleteBanner({ returnTo }: ProfileIncompleteBannerProps) {
  const location = useLocation();
  const ret = returnTo ?? location.pathname;
  const href = `/app/profilo?return=${encodeURIComponent(ret)}`;

  return (
    <Alert
      role="region"
      aria-label="Profilo di fatturazione incompleto"
      className="border-amber-200 bg-amber-50 text-amber-900"
    >
      <AlertTriangle className="h-4 w-4 text-amber-700" aria-hidden="true" />
      <AlertTitle>Profilo di fatturazione incompleto</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>
          Per procedere è necessario completare i dati anagrafici e di fatturazione del tuo
          account.
        </p>
        <Button
          asChild
          size="sm"
          className="bg-amber-700 text-white hover:bg-amber-800"
        >
          <Link to={href}>Completa il profilo</Link>
        </Button>
      </AlertDescription>
    </Alert>
  );
}
